import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getCurrentUser } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft,
  Package,
  GitBranch,
  History,
  Truck,
  Factory,
  FlaskConical,
  Wrench,
  User,
  Calendar,
  MapPin,
  FileText,
  Plus,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronRight,
  Layers,
} from "lucide-react";
import { AddMaterialDialog } from "./AddMaterialDialog";
import { AddEventDialog } from "./AddEventDialog";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface TraceabilityDetailsProps {
  batchId: string;
  onBack: () => void;
}

export function TraceabilityDetails({ batchId, onBack }: TraceabilityDetailsProps) {
  const { userRole } = useAuth();
  const queryClient = useQueryClient();
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  const { data: batch, isLoading } = useQuery({
    queryKey: ["production-batch", batchId],
    queryFn: async () => {
      const { data, error } = await api
        .from("production_batches")
        .select(`
          *,
          work_orders(id, order_number, product_name, customer, status),
          molds(id, code, name, description),
          machines(id, name, type),
          production_recipes(id, name, product_model_id)
        `)
        .eq("id", batchId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { data: materials } = useQuery({
    queryKey: ["batch-materials", batchId],
    queryFn: async () => {
      const { data, error } = await api
        .from("batch_materials")
        .select(`
          *,
          material_inventory(name, code, unit),
          profiles:added_by(full_name, email)
        `)
        .eq("batch_id", batchId)
        .order("added_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: events } = useQuery({
    queryKey: ["traceability-events", batchId],
    queryFn: async () => {
      const { data, error } = await api
        .from("traceability_events")
        .select(`
          *,
          profiles:performed_by(full_name, email)
        `)
        .eq("batch_id", batchId)
        .order("event_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: qualityInspections } = useQuery({
    queryKey: ["batch-quality", batchId],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_inspections")
        .select(`
          *,
          profiles:inspector_id(full_name, email)
        `)
        .eq("batch_id", batchId)
        .order("inspection_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: shipments } = useQuery({
    queryKey: ["batch-shipments", batchId],
    queryFn: async () => {
      const { data, error } = await api
        .from("shipment_items")
        .select(`
          *,
          shipments(shipment_number, customer_name, shipping_date, status, carrier)
        `)
        .eq("batch_id", batchId);

      if (error) throw error;
      return data;
    },
  });

  const canManage = userRole === "admin" || userRole === "supervisor" || userRole === "warehouse";

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; color: string; icon: any }> = {
      pending: { label: "Pendiente", color: "bg-gray-500/10 text-gray-500", icon: Clock },
      in_progress: { label: "En Proceso", color: "bg-blue-500/10 text-blue-500", icon: Factory },
      completed: { label: "Completado", color: "bg-green-500/10 text-green-500", icon: CheckCircle2 },
      cancelled: { label: "Cancelado", color: "bg-red-500/10 text-red-500", icon: AlertTriangle },
    };
    const { label, color, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={cn("gap-1", color)}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getEventIcon = (type: string) => {
    const icons: Record<string, any> = {
      created: Package,
      material_added: Layers,
      process_started: Factory,
      process_completed: CheckCircle2,
      quality_check: FlaskConical,
      shipped: Truck,
      note: FileText,
    };
    return icons[type] || History;
  };

  const getEventColor = (type: string) => {
    const colors: Record<string, string> = {
      created: "bg-blue-500",
      material_added: "bg-purple-500",
      process_started: "bg-yellow-500",
      process_completed: "bg-green-500",
      quality_check: "bg-orange-500",
      shipped: "bg-cyan-500",
      note: "bg-gray-500",
    };
    return colors[type] || "bg-gray-500";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Lote no encontrado</p>
        <Button onClick={onBack} className="mt-4">Volver</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{batch.batch_number}</h1>
            {getStatusBadge(batch.status)}
          </div>
          <p className="text-muted-foreground">
            {batch.work_orders?.product_name || "Sin producto asignado"}
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEventDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar Evento
            </Button>
            <Button onClick={() => setMaterialDialogOpen(true)}>
              <Layers className="w-4 h-4 mr-2" />
              Agregar Material
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Traceability Tree */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="w-5 h-5" />
                Arbol de Trazabilidad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Work Order */}
                {batch.work_orders && (
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-blue-500/5">
                    <FileText className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium">Orden de Trabajo</p>
                      <p className="text-sm text-muted-foreground">
                        {batch.work_orders.order_number} - {batch.work_orders.product_name}
                      </p>
                      {batch.work_orders.customer && (
                        <p className="text-xs text-muted-foreground">
                          Cliente: {batch.work_orders.customer}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}

                {/* Recipe */}
                {batch.production_recipes && (
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-purple-500/5 ml-6">
                    <FlaskConical className="w-5 h-5 text-purple-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium">Receta de Produccion</p>
                      <p className="text-sm text-muted-foreground">
                        {batch.production_recipes.name}
                      </p>
                    </div>
                  </div>
                )}

                {/* Materials Used */}
                {materials && materials.length > 0 && (
                  <div className="ml-6 space-y-2">
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Materiales Utilizados ({materials.length})
                    </p>
                    {materials.map((mat: any) => (
                      <div
                        key={mat.id}
                        className="flex items-center gap-3 p-2 rounded-lg border bg-muted/30 ml-4"
                      >
                        <Package className="w-4 h-4 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {mat.material_inventory?.name || mat.material_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Lote: {mat.material_lot_number || "-"} | Cantidad: {mat.quantity_used} {mat.unit}
                          </p>
                        </div>
                        {mat.supplier && (
                          <Badge variant="outline" className="text-xs">
                            {mat.supplier}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Mold */}
                {batch.molds && (
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-orange-500/5 ml-6">
                    <Wrench className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium">Molde</p>
                      <p className="text-sm text-muted-foreground">
                        {batch.molds.code} - {batch.molds.name}
                      </p>
                    </div>
                  </div>
                )}

                {/* Machine */}
                {batch.machines && (
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-green-500/5 ml-6">
                    <Factory className="w-5 h-5 text-green-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium">Maquina</p>
                      <p className="text-sm text-muted-foreground">
                        {batch.machines.name}
                        {batch.machines.type && ` (${batch.machines.type})`}
                      </p>
                    </div>
                  </div>
                )}

                {/* Quality Inspections */}
                {qualityInspections && qualityInspections.length > 0 && (
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-cyan-500/5 ml-6">
                    <FlaskConical className="w-5 h-5 text-cyan-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium">Inspecciones de Calidad ({qualityInspections.length})</p>
                      {qualityInspections.map((insp: any) => (
                        <div key={insp.id} className="flex items-center gap-2 mt-1">
                          <Badge
                            className={cn(
                              "text-xs",
                              insp.status === "passed" && "bg-green-500/10 text-green-500",
                              insp.status === "failed" && "bg-red-500/10 text-red-500"
                            )}
                          >
                            {insp.status === "passed" ? "Aprobada" : insp.status === "failed" ? "Rechazada" : insp.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(insp.inspection_date), "dd/MM/yyyy", { locale: es })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Shipments */}
                {shipments && shipments.length > 0 && (
                  <div className="flex items-start gap-3 p-3 rounded-lg border bg-indigo-500/5 ml-6">
                    <Truck className="w-5 h-5 text-indigo-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium">Envios ({shipments.length})</p>
                      {shipments.map((ship: any) => (
                        <div key={ship.id} className="text-sm text-muted-foreground mt-1">
                          {ship.shipments?.shipment_number} - {ship.shipments?.customer_name}
                          <Badge variant="outline" className="ml-2 text-xs">
                            {ship.shipments?.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Timeline de Eventos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {events && events.length > 0 ? (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
                  <div className="space-y-4">
                    {events.map((event: any) => {
                      const Icon = getEventIcon(event.event_type);
                      return (
                        <div key={event.id} className="relative pl-10">
                          <div
                            className={cn(
                              "absolute left-2 w-5 h-5 rounded-full flex items-center justify-center text-white",
                              getEventColor(event.event_type)
                            )}
                          >
                            <Icon className="w-3 h-3" />
                          </div>
                          <div className="p-3 rounded-lg border bg-muted/30">
                            <div className="flex items-center justify-between">
                              <Badge variant="outline">{event.event_type}</Badge>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(event.event_date), "dd/MM/yyyy HH:mm", { locale: es })}
                              </span>
                            </div>
                            {event.description && (
                              <p className="text-sm mt-2">{event.description}</p>
                            )}
                            {event.location && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </p>
                            )}
                            {event.profiles && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {event.profiles.full_name || event.profiles.email}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay eventos registrados</p>
                  {canManage && (
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => setEventDialogOpen(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Registrar Primer Evento
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Batch Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informacion del Lote</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cantidad</span>
                <span className="font-bold">{batch.quantity} uds</span>
              </div>
              <Separator />
              {batch.start_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Inicio</p>
                    <p className="text-sm font-medium">
                      {format(new Date(batch.start_date), "PPp", { locale: es })}
                    </p>
                  </div>
                </div>
              )}
              {batch.end_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Finalizacion</p>
                    <p className="text-sm font-medium">
                      {format(new Date(batch.end_date), "PPp", { locale: es })}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Creacion</p>
                  <p className="text-sm font-medium">
                    {format(new Date(batch.created_at), "PPp", { locale: es })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Materiales</span>
                <span className="font-medium">{materials?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Eventos</span>
                <span className="font-medium">{events?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Inspecciones</span>
                <span className="font-medium">{qualityInspections?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Envios</span>
                <span className="font-medium">{shipments?.length || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {batch.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notas</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{batch.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <AddMaterialDialog
        open={materialDialogOpen}
        onOpenChange={setMaterialDialogOpen}
        batchId={batchId}
      />

      <AddEventDialog
        open={eventDialogOpen}
        onOpenChange={setEventDialogOpen}
        batchId={batchId}
        workOrderId={batch.work_order_id}
      />
    </div>
  );
}
